import { useCallback, useMemo } from 'react'
import { Alert, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { DataTable } from '@/shared/ui/table/DataTable'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'
import { relativeFromNow } from './relativeTime'
import { fetchAuthLog, type AuthAttempt, type AuthOutcome } from './sessionApi'

interface AuthLogTableProps {
  /** Berilsa — faqat shu abonentniki va qidiruv maydoni chizilmaydi */
  username?: string
  /** Bitta sahifada ikkinchi jadval bo'lsa URL parametrlari to'qnashmasin */
  urlPrefix?: string
}

/**
 * Autentifikatsiya urinishlari jurnali (`radpostauth`).
 *
 * Bu jadval "kim ULANDI" emas, "kim ULANA OLMADI" degan savolga javob
 * beradi — sessiyalar tarixidan aynan shu bilan farq qiladi. Abonent
 * qo'ng'iroq qilib "internet ishlamayapti" desa, birinchi qaraladigan
 * joy shu: parol xatomi, qurilma boshqami, umuman urinish bormi.
 *
 * ⚠️ PAROL USTUNI YO'Q va bo'lmaydi (CLAUDE.md §7). Jadvalda
 * `pass` ustuni BOR va u to'ldirilgan, lekin backend uni javobga umuman
 * chiqarmaydi — `sessionApi.ts` dagi izohga qarang.
 *
 * ⚠️ JAMI SON KO'RSATILMAYDI: `SessionPage` da `total` yo'q, faqat
 * `hasNext` bor (tarix jadvalidagi bilan bir xil sabab).
 */
export function AuthLogTable({ username, urlPrefix }: AuthLogTableProps) {
  const { t } = useTranslation()

  const fetchPage = useCallback(
    (state: TableState, signal: AbortSignal): Promise<PageResult<AuthAttempt>> => {
      // Natija filtri ustun sarlavhasidan keladi va URL'da saqlanadi
      // (`?f.result=REJECTED`) — server tomonda filtrlanadi, mijozda emas.
      const selected = state.filters['result'] ?? []
      const outcome: AuthOutcome | undefined =
        selected.length === 1 && (selected[0] === 'ACCEPTED' || selected[0] === 'REJECTED')
          ? selected[0]
          : undefined

      return fetchAuthLog(state, signal, {
        ...(username === undefined ? {} : { username }),
        ...(outcome === undefined ? {} : { outcome }),
      })
    },
    [username],
  )

  const columns = useMemo<ColumnsType<AuthAttempt>>(() => {
    const base: ColumnsType<AuthAttempt> = []

    // Bitta abonent kartochkasida login ustuni ortiqcha — u sarlavhada bor
    if (username === undefined) {
      base.push({
        key: 'username',
        title: t('authLog.subscriber'),
        width: 180,
        fixed: 'left',
        render: (_, row) => (
          // Havola realmsiz login bilan: abonentlar ro'yxatida toza nom
          // turadi (`ActiveSessionsPanel` dagi bilan bir xil sabab)
          <Link to={`/subscribers?q=${encodeURIComponent(row.username)}`}>
            <Typography.Text strong>{row.username}</Typography.Text>
          </Link>
        ),
      })
    }

    return [
      ...base,
      {
        key: 'result',
        title: t('authLog.result'),
        width: 150,
        filterMultiple: false,
        filters: [
          { text: t('authLog.accept'), value: 'ACCEPTED' },
          { text: t('authLog.reject'), value: 'REJECTED' },
        ],
        render: (_, row) => (
          // Xom RADIUS javobi tooltip'da qoladi: FreeRADIUS matnga izoh
          // qo'shishi mumkin va u diagnostika uchun kerak bo'ladi
          <Tooltip title={row.reply ?? ''}>
            <Tag color={row.accepted ? 'green' : 'red'}>
              {row.accepted ? t('authReply.accept') : t('authReply.reject')}
            </Tag>
          </Tooltip>
        ),
      },
      {
        key: 'mac',
        title: t('authLog.mac'),
        width: 170,
        render: (_, row) => (
          <Typography.Text code={row.callingStationId !== null}>
            {row.callingStationId ?? '—'}
          </Typography.Text>
        ),
      },
      {
        key: 'calledStation',
        title: t('session.calledStation'),
        width: 150,
        render: (_, row) => row.calledStationId ?? '—',
      },
      {
        key: 'authDate',
        title: t('authLog.time'),
        width: 200,
        render: (_, row) =>
          row.authDate ? (
            <Tooltip title={utcTooltip(row.authDate)}>{formatDateTime(row.authDate)}</Tooltip>
          ) : (
            '—'
          ),
      },
      {
        key: 'ago',
        title: t('session.lastUpdate'),
        width: 130,
        render: (_, row) => (
          <Typography.Text type="secondary">{relativeFromNow(row.authDate, t)}</Typography.Text>
        ),
      },
    ]
  }, [t, username])

  return (
    <>
      {/* Ikki cheklov OCHIQ aytiladi — operator ko'rmagan narsasini "yo'q"
          deb o'ylamasin. Ikkalasi ham ataylab qilingan, nuqson emas. */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('authLog.note')}
        description={t('authLog.unknownLoginsNote')}
      />

      <DataTable<AuthAttempt>
        queryKey={[...queryKeys.sessions.authLog, username ?? 'all']}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => String(row.id)}
        searchable={username === undefined}
        searchPlaceholder={t('sessions.searchPlaceholder')}
        showTotal={false}
        scrollX={1000}
        emptyTitle={t('authLog.empty')}
        emptyDescription={t('authLog.emptyHint')}
        {...(urlPrefix === undefined ? {} : { urlPrefix })}
      />
    </>
  )
}
